import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../components/foundations/Icon';
import AttachmentChip from './AttachmentChip';
import { attachmentsApiPropType, isRetryableAttachment } from '../hooks/useAttachments';
import { ATTACHMENT_ACCEPT } from '../constants';
import css from './ChatInput.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';

// Icon paints its own color from the variant class, which outranks the button's inherited color,
// so the composer tints have to come through the inline style Icon spreads onto the svg.
const COMPOSER_ICON_STYLE = { color: 'var(--gray600)' };
const MIC_LISTENING_ICON_STYLE = { color: 'var(--red400)' };
const SEND_ICON_STYLE = { color: 'var(--white)' };
const SEND_ICON_DISABLED_STYLE = { color: 'var(--gray500)' };

const ChatInput = memo(
    ({
        input,
        handleInputChange,
        handleSubmit,
        isLoading,
        stop,
        onMicClick,
        isMicListening = false,
        micSupported = true,
        containerClassName,
        inputRef: externalInputRef = null,
        attachments = null,
        translate,
    }) => {
        const localInputRef = useRef(null);
        const inputRef = externalInputRef || localInputRef;
        const fileInputRef = useRef(null);
        const [isDragOver, setIsDragOver] = useState(false);

        const attachmentList = attachments?.attachments || [];
        const isUploading = Boolean(attachments?.isUploading);
        const hasReadyAttachments = Boolean(attachments?.readyUploadIds?.length);

        // Auto-resize the textarea up to its CSS max-height.
        const autoResize = el => {
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
        };

        useEffect(() => {
            inputRef.current?.focus();
        }, [inputRef]);

        // Re-run resize when value changes from outside (e.g. mic transcript appends).
        useEffect(() => {
            autoResize(inputRef.current);
        }, [input, inputRef]);

        const handleChange = e => {
            autoResize(e.target);
            handleInputChange(e);
        };

        const canSubmit = (input.trim() || hasReadyAttachments) && !isLoading && !isUploading;

        const handleKeyDown = e => {
            // Submit on Enter (without Shift)
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSubmit) handleSubmit(e);
            }
        };

        const handleFormSubmit = e => {
            e.preventDefault();
            if (canSubmit) {
                handleSubmit(e);
            }
        };

        const focusInputFromWrapper = e => {
            if (e.target.closest('button, textarea, input, a')) return;
            e.preventDefault();
            inputRef.current?.focus();
        };

        const handleMicClick = () => {
            onMicClick?.();
            const el = inputRef.current;
            if (!el) return;
            const end = el.value.length;
            el.focus();
            el.setSelectionRange(end, end);
        };

        const handleFilesPicked = e => {
            attachments?.addFiles(e.target.files);
            // Allow re-picking the same file after removing it.
            e.target.value = '';
        };

        const handleDrop = e => {
            if (!attachments) return;
            e.preventDefault();
            setIsDragOver(false);
            if (!isLoading && e.dataTransfer?.files?.length) {
                attachments.addFiles(e.dataTransfer.files);
            }
        };

        const handleDragOver = e => {
            if (!attachments) return;
            e.preventDefault();
            setIsDragOver(true);
        };

        // dragleave bubbles from children, so the highlight would otherwise
        // flicker whenever the pointer crossed onto the textarea.
        const handleDragLeave = e => {
            if (e.currentTarget.contains(e.relatedTarget)) return;
            setIsDragOver(false);
        };

        const handlePaste = e => {
            if (!attachments || isLoading) return;
            const files = Array.from(e.clipboardData?.files || []);
            if (files.length) {
                e.preventDefault();
                attachments.addFiles(files);
            }
        };

        return (
            <div
                className={classNames(css.chatInputContainer, containerClassName, { [css.dragOver]: isDragOver })}
                onMouseDown={focusInputFromWrapper}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <form onSubmit={handleFormSubmit} className={css.chatInputForm}>
                    <div className={css.inputContainer}>
                        {attachmentList.length > 0 && (
                            <div className={css.attachmentsRow}>
                                {attachmentList.map(att => (
                                    <AttachmentChip
                                        key={att.localId}
                                        name={att.name}
                                        kind={att.kind}
                                        sizeBytes={att.sizeBytes}
                                        status={att.status}
                                        errorKey={att.error}
                                        errorMessage={att.errorMessage}
                                        previewUrl={att.previewUrl}
                                        onRemove={() => attachments.removeAttachment(att.localId)}
                                        onRetry={
                                            isRetryableAttachment(att)
                                                ? () => attachments.retryAttachment(att.localId)
                                                : null
                                        }
                                    />
                                ))}
                            </div>
                        )}
                        <div className={css.inputHeader}>
                            <textarea
                                ref={inputRef}
                                rows={1}
                                className={css.inputField}
                                placeholder={translate(
                                    isLoading
                                        ? `${TRANSLATION_PREFIX}.INPUT_PLACEHOLDER_LOADING`
                                        : `${TRANSLATION_PREFIX}.INPUT_PLACEHOLDER`
                                )}
                                value={input}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                disabled={isLoading}
                            />
                            <div className={css.inputHeaderIcons}>
                                {attachments && (
                                    <>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept={ATTACHMENT_ACCEPT}
                                            className={css.hiddenFileInput}
                                            onChange={handleFilesPicked}
                                        />
                                        <button
                                            type="button"
                                            className={css.iconButton}
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isLoading}
                                            aria-label={translate(`${TRANSLATION_PREFIX}.ATTACHMENTS.ATTACH_LABEL`)}
                                            title={translate(`${TRANSLATION_PREFIX}.ATTACHMENTS.ATTACH_LABEL`)}
                                        >
                                            <Icon
                                                name="paperclip"
                                                type={IconTypes.TABLER}
                                                size={IconSizes.MEDIUM}
                                                style={COMPOSER_ICON_STYLE}
                                            />
                                        </button>
                                    </>
                                )}
                                {micSupported && (
                                    <button
                                        type="button"
                                        className={classNames(css.iconButton, {
                                            [css.micListening]: isMicListening,
                                        })}
                                        onClick={handleMicClick}
                                        disabled={isLoading}
                                        aria-label={translate(
                                            isMicListening
                                                ? `${TRANSLATION_PREFIX}.MIC_STOP_LISTENING_LABEL`
                                                : `${TRANSLATION_PREFIX}.MIC_LABEL`
                                        )}
                                        title={translate(
                                            isMicListening
                                                ? `${TRANSLATION_PREFIX}.MIC_STOP_LISTENING_LABEL`
                                                : `${TRANSLATION_PREFIX}.MIC_LABEL`
                                        )}
                                        aria-pressed={isMicListening}
                                    >
                                        <Icon
                                            name={isMicListening ? 'mic-fill' : 'mic-line'}
                                            type="mingcute"
                                            disabled={isLoading}
                                            size={IconSizes.MEDIUM}
                                            style={isMicListening ? MIC_LISTENING_ICON_STYLE : COMPOSER_ICON_STYLE}
                                        />
                                    </button>
                                )}
                                {isLoading ? (
                                    <button
                                        type="button"
                                        className={classNames(css.iconButton, css.sendButton)}
                                        onClick={stop}
                                        aria-label={translate(`${TRANSLATION_PREFIX}.STOP_LABEL`)}
                                        title={translate(`${TRANSLATION_PREFIX}.STOP_LABEL`)}
                                    >
                                        <Icon
                                            name="square"
                                            type={IconTypes.TABLER}
                                            size={IconSizes.SMALL}
                                            variant={IconVariants.WHITE}
                                        />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={!canSubmit}
                                        className={classNames(css.iconButton, css.sendButton)}
                                        aria-label={translate(`${TRANSLATION_PREFIX}.SEND_LABEL`)}
                                        title={translate(`${TRANSLATION_PREFIX}.SEND_LABEL`)}
                                    >
                                        <Icon
                                            name="arrow-up-fill"
                                            type="mingcute"
                                            size={IconSizes.SMALL}
                                            interactive={false}
                                            style={canSubmit ? SEND_ICON_STYLE : SEND_ICON_DISABLED_STYLE}
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        );
    }
);

ChatInput.propTypes = {
    input: PropTypes.string.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    stop: PropTypes.func.isRequired,
    onMicClick: PropTypes.func,
    isMicListening: PropTypes.bool,
    micSupported: PropTypes.bool,
    containerClassName: PropTypes.string,
    inputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    attachments: attachmentsApiPropType,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ChatInput);
